import React, { useState } from 'react';
import { IcUser, IcClose, IcSave, IcKeyRound } from '../../theme/icons';
import sellerIdentityService from '../../services/sellerIdentityService';

// Fiche d'une PERSONNE (nom, niveau, boutique, code) en popup —
// partagée par Paramètres › Personnes et Paramètres › Boutiques (owner
// 2026-09-29 : tout se gère depuis la carte de la boutique).
//   person : la personne à modifier, ou null pour en ajouter une
//   preset : valeurs de départ d'un ajout (ex. { level: 'manager' })
//   onSaved() après Enregistrer
export const LEVELS = {
  seller: { label: 'Vendeur', badge: 'bdg-neutral', min: 1, desc: 'Vend et encaisse, avec les limites de la caisse. Code aussi simple que voulu (« 1 », « k »…).' },
  manager: { label: 'Chef d’équipe', badge: 'bdg-pos', min: 2, desc: 'Dans sa boutique, son code valide un retour client. Rien d’autre : le reste demande un compte manager.' },
  owner: { label: 'Propriétaire', badge: 'bdg-warn', min: 2, desc: 'Comme un manager, dans toutes les boutiques.' },
};
const inputClass = 'inp w-full pl-11 pr-3 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';
const blank = { id: null, name: '', level: 'seller', is_active: true, pin: '', has_pin: false };

const PersonFormModal = ({ person, preset, onSaved, onClose }) => {
  const [form, setForm] = useState(() => (person ? { ...blank, ...person, pin: '' } : { ...blank, ...(preset || {}) }));
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setFormError('');
    const min = LEVELS[form.level].min;
    if (!form.name.trim()) { setFormError('Le nom est requis.'); return; }
    // Le code est obligatoire (owner 2026-10-03) : requis à l'ajout ; en modification, vide = inchangé.
    if (!form.id && !form.pin) { setFormError('Le code est requis : chaque personne en a un.'); return; }
    if (form.pin && form.pin.length < min) { setFormError(`Le code d’un ${LEVELS[form.level].label.toLowerCase()} comporte au moins ${min} caractères.`); return; }
    // La boutique se range dans Paramètres › Boutiques, pas ici.
    const payload = { id: form.id, name: form.name.trim(), level: form.level, is_active: form.is_active };
    if (form.pin) payload.pin = form.pin;          // tapé = nouveau code ; sinon inchangé
    try {
      setSaving(true);
      await sellerIdentityService.savePerson(payload);
      onSaved && onSaved();
    } catch (err) {
      setFormError(err.detail || err.pin?.[0] || err.stores?.[0] || err.name?.[0] || "Impossible d'enregistrer la personne.");
    } finally {
      setSaving(false);
    }
  };


  const level = LEVELS[form.level];

  return (
    <>
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-8 overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">{form.id ? 'Modifier la personne' : 'Nouvelle personne'}</h3>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              {formError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" role="alert">{formError}</div>}

              <div>
                <label className={labelClass} htmlFor="person-name">Nom <span className="req">*</span></label>
                <div className="relative">
                  <IcUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input id="person-name" className={inputClass} type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex : Sara" autoComplete="off" required />
                </div>
              </div>

              <div>
                <span className={labelClass}>Niveau</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Object.entries(LEVELS).map(([value, l]) => {
                    const selected = form.level === value;
                    return (
                      <button key={value} type="button" aria-pressed={selected} onClick={() => setForm({ ...form, level: value })}
                        className={`p-3 rounded-xl border-2 text-left transition ${selected ? 'border-gray-900 bg-white text-gray-900' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                        <span className="block text-sm font-semibold">{l.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-gray-400">{level.desc}</p>
              </div>

              {form.level === 'owner' ? (
                <p className="text-xs text-gray-400 -mt-2">Un propriétaire est reconnu dans toutes les boutiques.</p>
              ) : (
                <p className="text-xs text-gray-500 -mt-2">
                  <span className="font-medium text-gray-700">{form.level === 'manager' ? 'Boutiques : ' : 'Boutique : '}</span>
                  {(form.store_names || []).length ? form.store_names.join(', ') : 'aucune pour l’instant'}
                  <span className="text-gray-400"> — se range dans Paramètres › Boutiques.</span>
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="person-pin">
                    Code {form.id ? <span className="text-gray-400 font-normal">(défini — vide = inchangé)</span> : <span className="req">*</span>}
                  </label>
                  <div className="relative">
                    <IcKeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input id="person-pin" className={inputClass} type="text" maxLength={12} value={form.pin}
                      onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 12) })}
                      placeholder={level.min > 1 ? `au moins ${level.min} caractères` : 'ex : 1, k, sara'} autoComplete="off" autoCapitalize="off" spellCheck={false} />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">Lettres ou chiffres, tapés au clavier{level.min > 1 ? ` — ${level.min} caractères minimum : ce code approuve des actions réservées` : ', aussi court que vous voulez'}. Majuscule ou minuscule, c’est pareil.</p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-2">
                <button type="button" onClick={onClose} className="btn-g px-5 py-2.5 transition">Annuler</button>
                <button type="submit" disabled={saving} className={`${form.id ? 'btn-p' : 'btn-add-pop'} gap-2 px-5 py-2.5 transition`}><IcSave /> {saving ? 'Enregistrement…' : (form.id ? 'Enregistrer' : 'Ajouter la personne')}</button>
              </div>
            </form>
          </div>
        </div>
    </>
  );
};

export default PersonFormModal;
